import { useCallback, useContext } from 'react';

import { request } from '@/renderer/utils/api';
import { InfiniteStoreContext, useInfiniteStore } from './InfiniteStoreProvider';
import type { InfiniteStore } from './types';

/**
 * Warm an infinite-request store bank before its component mounts (e.g. on hover),
 * so the component renders the data on its first frame instead of a loader.
 *
 * The prefetch writes the SAME bank `useNextInfiniteRequest` reads, under the same
 * key, so there is no second cache to keep in sync. A hook that mounts while its
 * key's prefetch is in flight waits for it instead of issuing the same request
 * again, and a hook that mounts on a just-prefetched bank skips its revalidation
 * (see `consumeFreshPrefetch`).
 */

// How long a prefetched bank counts as fresh enough to open without revalidating.
// Short on purpose: it only has to cover hover -> click.
const PREFETCH_FRESH_MS = 3000;

const inflight = new Map<string, Promise<void>>();
const seededAt = new Map<string, number>();

/** The store key `useNextInfiniteRequest` uses for an opts `key`. */
export function infiniteStoreKey(key: string) {
  return `∞${key.includes(':') ? key : `${key}:infinity`}`;
}

/** The key's prefetch, while its request is still in flight. */
export function pendingPrefetch(storeKey: string) {
  return inflight.get(storeKey);
}

/** Whether the key was prefetched moments ago (without consuming it). */
export function isFreshPrefetch(storeKey: string) {
  const at = seededAt.get(storeKey);
  return at !== undefined && Date.now() - at < PREFETCH_FRESH_MS;
}

/**
 * True (once) when the key was prefetched moments ago, so the mounting hook can use
 * it as-is. Consumed on read: a later mount of the same key revalidates normally.
 */
export function consumeFreshPrefetch(storeKey: string) {
  const fresh = isFreshPrefetch(storeKey);
  seededAt.delete(storeKey);
  return fresh;
}

/** Forget a prefetch's freshness, e.g. when a live event shows the data moved on. */
export function invalidatePrefetch(storeKey: string) {
  seededAt.delete(storeKey);
}

type StoreGet = InfiniteStore<API.DbItem, unknown>['get'];
type StoreSet = InfiniteStore<API.DbItem, unknown>['set'];

function prefetchInto(
  storeGet: StoreGet,
  storeSet: StoreSet,
  storeKey: string,
  input: string,
  query: Record<string, unknown>,
) {
  // Already loaded (it revalidates when it mounts) or already on its way.
  if (storeGet(storeKey)?.data || inflight.has(storeKey)) {
    return inflight.get(storeKey) ?? Promise.resolve();
  }

  const run = (async () => {
    try {
      const json = await request<API.WithPage<API.DbItem[]>>(
        { input, init: {} },
        { query: query as never, hideAlert: true },
      );
      if (!Array.isArray(json?.data) || storeGet(storeKey)?.data) {
        return;
      }
      // The raw page is exactly the bank's shape (InfiniteStoreProvider seeds
      // SSR data the same way).
      storeSet(storeKey, (state) => {
        state.data = json as never;
      });
      seededAt.set(storeKey, Date.now());
    } catch {
      // A failed prefetch is not an error: the component loads normally on open.
    } finally {
      inflight.delete(storeKey);
    }
  })();

  inflight.set(storeKey, run);
  return run;
}

export function usePrefetchInfinite() {
  const storeGet = useInfiniteStore((state: InfiniteStore<API.DbItem, unknown>) => state.get);
  const storeSet = useInfiniteStore((state: InfiniteStore<API.DbItem, unknown>) => state.set);

  return useCallback(
    (storeKey: string, input: string, query: Record<string, unknown>) =>
      prefetchInto(storeGet, storeSet, storeKey, input, query),
    [storeGet, storeSet],
  );
}

/**
 * `usePrefetchInfinite` for chrome that can render outside the store (e.g. in a
 * test): without a provider there is nothing to warm, so it does nothing.
 */
export function useOptionalPrefetchInfinite() {
  const store = useContext(InfiniteStoreContext);

  return useCallback(
    (storeKey: string, input: string, query: Record<string, unknown>) => {
      if (!store) {
        return Promise.resolve();
      }
      const { get, set } = store.getState() as unknown as InfiniteStore<API.DbItem, unknown>;
      return prefetchInto(get, set, storeKey, input, query);
    },
    [store],
  );
}
